// Public subscription activation (engine v2). All reads/writes go through the
// activate-subscription edge function keyed by the unguessable link token —
// this page has no direct table access.
//
//   card mode    — customer reviews terms, signs (if required), and puts a
//                  card on file via a Stripe SetupIntent (no redirect). On
//                  completion the server activates the subscription and, when
//                  billing starts today, immediately runs the first cycle
//                  (invoice + card charge).
//   invoice mode — review + sign only; each period generates an invoice that
//                  the business collects.
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Trash2, CheckCircle2, AlertCircle, CreditCard } from 'lucide-react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { supabase } from '../../lib/supabase';
import { invokeErrorMessage } from '../../lib/supabaseFunctionError';
import { formatTaxCents, formatTaxRate, quoteSubscriptionTax, SubscriptionTaxRate } from '../../lib/subscriptionTax';

interface SubscriptionData {
  id: string;
  name: string;
  description: string | null;
  amount: number;
  currency: string;
  interval: string;
  interval_count: number;
  billing_start: string;
  custom_start_date: string | null;
  duration: string;
  custom_end_date: string | null;
  require_signature: boolean;
  status: string;
  collection_mode: string;
  subscription_number: string | null;
  taxes?: SubscriptionTaxRate[];
}

interface PageData {
  subscription: SubscriptionData;
  business: { name?: string; booking_settings?: { logo_url?: string; navbar_color?: string } | null };
  hasCard: boolean;
  stripeReady: boolean;
}

function unwrapInvoke<T>(data: unknown): T | null {
  if (!data || typeof data !== 'object') return null;
  const o = data as Record<string, unknown>;
  if ('data' in o && o.data && typeof o.data === 'object') return o.data as T;
  return o as T;
}

function formatInterval(interval: string, count: number): string {
  if (count === 1) {
    switch (interval) {
      case 'day': return 'Daily';
      case 'week': return 'Weekly';
      case 'month': return 'Monthly';
      case 'year': return 'Yearly';
      default: return interval;
    }
  }
  const unit = interval === 'day' ? 'days' : interval === 'week' ? 'weeks' : interval === 'month' ? 'months' : 'years';
  return `Every ${count} ${unit}`;
}

function ActivateForm({
  token,
  data,
  brandColor,
  setupInfo,
  onActivated,
}: {
  token: string;
  data: PageData;
  brandColor: string;
  setupInfo: { clientSecret: string; setupIntentId: string } | null;
  onActivated: (outcome: { collection_mode: string; first_bill_date: string; charged: boolean }) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const sub = data.subscription;
  const needsCardEntry = sub.collection_mode === 'card' && !data.hasCard;
  const taxQuote = quoteSubscriptionTax(Number(sub.amount) || 0, sub.taxes || []);
  const dueNow = taxQuote.taxCents > 0 ? taxQuote.totalCents / 100 : Number(sub.amount);

  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);

  // Signature state
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);

  // High-DPI signature canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !sub.require_signature) return;
    const setup = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(dpr, dpr);
        ctx.strokeStyle = '#111827';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    };
    setup();
    window.addEventListener('resize', setup);
    return () => window.removeEventListener('resize', setup);
  }, [sub.require_signature]);

  const coords = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top };
  };
  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = coords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };
  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = coords(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSigned(true);
  };
  const stopDrawing = (e: React.MouseEvent | React.TouchEvent) => { e.preventDefault(); setIsDrawing(false); };
  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    setHasSigned(false);
  };

  const canActivate =
    (!sub.require_signature || hasSigned) &&
    (!needsCardEntry || (cardComplete && !!setupInfo && !!stripe));

  const handleActivate = async () => {
    setActivating(true);
    setError(null);
    try {
      let setupIntentId: string | undefined;

      if (needsCardEntry) {
        if (!stripe || !elements || !setupInfo) throw new Error('Payment form is not ready yet');
        const card = elements.getElement(CardElement);
        if (!card) throw new Error('Card field not found');
        const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(setupInfo.clientSecret, {
          payment_method: { card },
        });
        if (confirmError) throw new Error(confirmError.message);
        if (setupIntent?.status !== 'succeeded') throw new Error('Card setup was not completed');
        setupIntentId = setupInfo.setupIntentId;
      }

      const signature = sub.require_signature && canvasRef.current
        ? canvasRef.current.toDataURL('image/png')
        : undefined;

      const { data: raw, error: fnErr } = await supabase.functions.invoke('activate-subscription', {
        body: { action: 'complete', token, setupIntentId, signature },
      });
      if (fnErr) throw new Error((await invokeErrorMessage(fnErr, raw as { error?: unknown })) || 'Could not activate this subscription');
      const res = unwrapInvoke<{ activated?: boolean; collection_mode?: string; first_bill_date?: string; firstCycle?: { outcomes?: Array<{ result?: string }> }; error?: string }>(raw);
      if (res?.error) throw new Error(res.error);
      if (!res?.activated) throw new Error('Activation did not complete');

      const charged = (res.firstCycle?.outcomes || []).some((o) => o?.result === 'charged');
      onActivated({
        collection_mode: res.collection_mode || sub.collection_mode,
        first_bill_date: res.first_bill_date || '',
        charged,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setActivating(false);
    }
  };

  return (
    <>
      {/* Card on file (card mode without an existing card) */}
      {needsCardEntry && (
        <div className="px-6 pt-4 pb-2">
          <label className="text-sm font-semibold text-gray-700 mb-2 block">Card for automatic payments</label>
          <div className="px-4 py-3.5 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl focus-within:border-solid focus-within:border-blue-500 focus-within:bg-white transition-all">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#374151',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    '::placeholder': { color: '#9CA3AF' },
                  },
                  invalid: { color: '#EF4444', iconColor: '#EF4444' },
                },
                hidePostalCode: true,
                disableLink: true,
              }}
              onChange={(ev) => {
                setCardComplete(ev.complete);
                if (ev.error?.message) setError(ev.error.message);
                else setError(null);
              }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Stored securely with Stripe. Each billing period is charged to this card automatically.
          </p>
        </div>
      )}

      {sub.collection_mode === 'card' && data.hasCard && (
        <div className="px-6 pt-4 pb-2">
          <div className="flex items-center gap-2.5 rounded-xl border border-green-200 bg-green-50 px-4 py-3">
            <CreditCard className="h-4 w-4 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800">A card is already on file — payments will be automatic.</p>
          </div>
        </div>
      )}

      {/* Signature */}
      {sub.require_signature && (
        <div className="px-6 pt-4 pb-2">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-gray-700">Signature</label>
            {hasSigned && (
              <button onClick={clearSignature} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors">
                <Trash2 className="h-3 w-3" />
                Clear
              </button>
            )}
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-gray-50 relative">
            <canvas
              ref={canvasRef}
              className="w-full touch-none"
              style={{ height: 140, cursor: 'crosshair' }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
            {!hasSigned && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-sm text-gray-400">Sign here</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action */}
      <div className="px-6 py-6 space-y-3">
        <button
          disabled={!canActivate || activating}
          onClick={handleActivate}
          style={{ backgroundColor: canActivate && !activating ? brandColor : undefined }}
          className={`w-full px-4 py-3.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 active:scale-[0.98] ${
            canActivate && !activating ? 'text-white hover:opacity-90' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {activating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Activating...
            </>
          ) : sub.collection_mode === 'card' && sub.billing_start === 'immediate' ? (
            `Activate — pay $${dueNow.toFixed(2)} now`
          ) : (
            'Activate Subscription'
          )}
        </button>
        {error && (
          <div className="flex items-start gap-2 text-xs text-red-600 justify-center">
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </>
  );
}

export default function ActivateSubscription() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [setupInfo, setSetupInfo] = useState<{ clientSecret: string; setupIntentId: string } | null>(null);
  const [activated, setActivated] = useState<{ collection_mode: string; first_bill_date: string; charged: boolean } | null>(null);

  useEffect(() => {
    (async () => {
      if (!token) {
        setLoadError('Invalid subscription link');
        setLoading(false);
        return;
      }
      try {
        const { data: raw, error: fnErr } = await supabase.functions.invoke('activate-subscription', {
          body: { action: 'get', token },
        });
        if (fnErr) {
          throw new Error((await invokeErrorMessage(fnErr, raw as { error?: unknown })) || 'Could not open this subscription link');
        }
        const res = unwrapInvoke<PageData & { error?: string }>(raw);
        if (res?.error) throw new Error(res.error);
        if (!res?.subscription) throw new Error('Subscription not found');
        setData(res);

        // Card entry confirms a SetupIntent on the business's Connect account,
        // so Stripe.js must load scoped to that account.
        if (res.subscription.collection_mode === 'card' && !res.hasCard && res.subscription.status === 'pending') {
          const { data: setupRaw } = await supabase.functions.invoke('activate-subscription', {
            body: { action: 'begin_card_setup', token },
          });
          const setup = unwrapInvoke<{ stripeAccountId?: string; clientSecret?: string; setupIntentId?: string }>(setupRaw);
          const platformKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
          if (setup?.stripeAccountId && setup?.clientSecret && setup?.setupIntentId && platformKey) {
            setSetupInfo({ clientSecret: setup.clientSecret, setupIntentId: setup.setupIntentId });
            setStripePromise(loadStripe(platformKey, { stripeAccount: setup.stripeAccountId } as Parameters<typeof loadStripe>[1]));
          }
        }
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Something went wrong');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (loadError || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4"
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}
      >
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {loadError === 'Subscription not found' ? 'Subscription Not Found' : 'This link could not be opened'}
          </h1>
          <p className="text-sm text-gray-500">{loadError || 'This link may have expired or is invalid.'}</p>
        </div>
      </div>
    );
  }

  const sub = data.subscription;
  const business = data.business;
  const businessName = business?.name || '';
  const logoUrl = business?.booking_settings?.logo_url;
  const brandColor = business?.booking_settings?.navbar_color || '#2563EB';

  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : null;
  };
  const rgb = hexToRgb(brandColor);
  const brandColorLight = rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.08)` : 'rgba(37, 99, 235, 0.08)';

  const currencySymbol = (sub.currency || 'usd').toUpperCase() === 'CAD' ? 'CA$'
    : (sub.currency || 'usd').toUpperCase() === 'EUR' ? '\u20AC'
    : (sub.currency || 'usd').toUpperCase() === 'GBP' ? '\u00A3'
    : '$';

  const isAlreadyActive = sub.status === 'active' || sub.status === 'past_due' || !!activated;
  const isCancelled = sub.status === 'cancelled' || sub.status === 'expired';
  const isCardMode = sub.collection_mode === 'card';
  const taxQuote = quoteSubscriptionTax(Number(sub.amount) || 0, sub.taxes || []);
  const hasTax = taxQuote.taxCents > 0;
  const totalAmount = hasTax ? taxQuote.totalCents / 100 : Number(sub.amount);

  const form = (
    <ActivateForm
      token={token!}
      data={data}
      brandColor={brandColor}
      setupInfo={setupInfo}
      onActivated={setActivated}
    />
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}
    >
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200/80 max-w-md w-full overflow-hidden">
        {/* Business Header */}
        <div className="px-6 pt-7 pb-5 text-center border-b border-gray-100">
          {logoUrl ? (
            <img src={logoUrl} alt={businessName} className="h-10 max-w-[180px] object-contain mx-auto mb-3" />
          ) : businessName ? (
            <h2 className="text-lg font-bold text-gray-900 mb-1">{businessName}</h2>
          ) : null}
          {logoUrl && businessName && <p className="text-xs text-gray-400 font-medium">{businessName}</p>}
        </div>

        {/* Subscription Title Card */}
        <div className="mx-6 mt-5 rounded-xl px-5 py-5 text-center" style={{ backgroundColor: brandColorLight }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: brandColor }}>
            Subscription{sub.subscription_number ? ` · ${sub.subscription_number}` : ''}
          </p>
          <h1 className="text-xl font-bold text-gray-900">{sub.name}</h1>
          {sub.description && <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">{sub.description}</p>}
          <div className="mt-4">
            <span className="text-3xl font-extrabold text-gray-900" style={{ letterSpacing: '-0.03em' }}>
              {currencySymbol}{totalAmount.toFixed(2)}
            </span>
            <span className="text-sm text-gray-400 ml-1">
              / {formatInterval(sub.interval, sub.interval_count).toLowerCase()}
            </span>
            {hasTax && (
              <div className="mt-3 text-left bg-white/70 rounded-lg px-3 py-2 space-y-1">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Amount</span>
                  <span>{currencySymbol}{formatTaxCents(taxQuote.netCents)}</span>
                </div>
                {taxQuote.lines.map((line) => (
                  <div key={`${line.name}-${line.rate}`} className="flex justify-between text-xs text-gray-500">
                    <span>{line.name} ({formatTaxRate(line.rate)}%)</span>
                    <span>{currencySymbol}{formatTaxCents(line.amountCents)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="px-6 pt-5 pb-2 space-y-0">
          <div className="flex justify-between items-center py-3 border-b border-gray-100">
            <span className="text-sm text-gray-500">Frequency</span>
            <span className="text-sm font-semibold text-gray-900">
              {formatInterval(sub.interval, sub.interval_count)}
            </span>
          </div>
          <div className="flex justify-between items-center py-3 border-b border-gray-100">
            <span className="text-sm text-gray-500">Billing starts</span>
            <span className="text-sm font-semibold text-gray-900">
              {sub.billing_start === 'immediate'
                ? 'Upon activation'
                : sub.custom_start_date
                  ? new Date(sub.custom_start_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                  : 'Custom date'}
            </span>
          </div>
          <div className="flex justify-between items-center py-3 border-b border-gray-100">
            <span className="text-sm text-gray-500">Duration</span>
            <span className="text-sm font-semibold text-gray-900">
              {sub.duration === 'forever'
                ? 'Until cancelled'
                : sub.custom_end_date
                  ? `Until ${new Date(sub.custom_end_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
                  : 'Custom end date'}
            </span>
          </div>
          <div className="flex justify-between items-center py-3 border-b border-gray-100">
            <span className="text-sm text-gray-500">Payment</span>
            <span className="text-sm font-semibold text-gray-900">
              {isCardMode ? 'Automatic — card on file' : 'Invoiced each period'}
            </span>
          </div>
        </div>

        {/* Body */}
        {activated || isAlreadyActive ? (
          <div className="px-6 py-6">
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-center">
              <CheckCircle2 className="h-9 w-9 text-green-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-green-800">
                {activated ? 'Your subscription is now active!' : 'This subscription is already active'}
              </p>
              {activated?.charged && (
                <p className="text-xs text-green-600 mt-1">First payment confirmed. You're all set.</p>
              )}
              {activated && !activated.charged && activated.first_bill_date && (
                <p className="text-xs text-green-600 mt-1">
                  {activated.collection_mode === 'card'
                    ? `First payment will be charged on ${new Date(activated.first_bill_date + 'T00:00:00').toLocaleDateString()}.`
                    : 'You\u2019ll receive an invoice each billing period.'}
                </p>
              )}
            </div>
          </div>
        ) : isCancelled ? (
          <div className="px-6 py-6">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 text-center">
              <p className="text-sm font-semibold text-gray-600">This subscription is no longer available</p>
            </div>
          </div>
        ) : isCardMode && !data.hasCard && !data.stripeReady ? (
          <div className="px-6 py-6">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
              <p className="text-sm font-semibold text-amber-800">Online payments aren't available yet</p>
              <p className="text-xs text-amber-600 mt-1">Please contact {businessName || 'the business'} to finish setting up.</p>
            </div>
          </div>
        ) : isCardMode && !data.hasCard && !stripePromise ? (
          <div className="px-6 py-8 flex items-center justify-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading secure payment form…
          </div>
        ) : (
          /* The form's Stripe hooks need an Elements provider even when no
             card entry is shown (invoice mode / card already on file). */
          <Elements
            stripe={isCardMode && !data.hasCard ? stripePromise : null}
            options={{ appearance: { theme: 'stripe' }, loader: 'auto' }}
          >
            {form}
          </Elements>
        )}

        {/* Footer */}
        {businessName && (
          <div className="border-t border-gray-100 px-6 py-4 text-center">
            <p className="text-xs text-gray-400">
              Subscription by <span className="font-medium text-gray-500">{businessName}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
